/**
 * Task 1a - Tracking pixels admin CMS.
 *
 * Route: /admin/pixels (super_admin only)
 *
 * Sections:
 *   AddPixelCard       - create form (provider select + pixel_id + token + events)
 *   PixelsTable        - list w/ enabled toggle + edit + delete + Test & Verify
 *   PixelEditDrawer    - shadcn Sheet to edit an existing pixel
 *   PixelTestLogPanel  - collapsible trace log w/ Download JSON
 *
 * Providers:
 *   meta / x            - live CAPI test event
 *   google / tiktok     - schema-only, /test returns a stub trace
 *
 * Ember / navy palette. Zero em / en-dashes. Middle-dot separators only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Radar, PlayCircle, Trash2, PenLine, Download, ChevronDown, ChevronRight,
  ShieldCheck, KeyRound, CircleAlert, CheckCircle2, RefreshCw, Loader2, XCircle,
} from 'lucide-react';
import api from '@/utils/api';

const PROVIDER_LABELS = {
  meta: 'Meta (Facebook / Instagram)',
  x: 'X (Twitter)',
  google: 'Google Ads',
  tiktok: 'TikTok',
};

const PROVIDER_LIVE = new Set(['meta', 'x']);

const PROVIDER_BADGE = {
  meta: 'bg-blue-50 text-blue-800 border-blue-200',
  x: 'bg-neutral-100 text-neutral-800 border-neutral-300',
  google: 'bg-amber-50 text-amber-800 border-amber-200',
  tiktok: 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200',
};

// ---------------------------------------------------------------------------
// Add-pixel card
// ---------------------------------------------------------------------------
const AddPixelCard = ({ providersLive, providersInert, standardEvents, onCreated }) => {
  const [provider, setProvider] = useState('meta');
  const [pixelId, setPixelId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState(['PageView']);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const providers = [...(providersLive || []), ...(providersInert || [])];
  const toggleEvent = (ev) => {
    setSelectedEvents((cur) => cur.includes(ev)
      ? cur.filter(x => x !== ev)
      : [...cur, ev]);
  };

  const submit = async () => {
    if (!pixelId.trim()) {
      toast.error('Pixel ID is required.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/admin/pixels', {
        provider, pixel_id: pixelId.trim(),
        access_token: accessToken.trim() || null,
        enabled, events: selectedEvents,
        notes: notes.trim(),
      });
      toast.success(`Pixel created: ${PROVIDER_LABELS[provider]}`);
      setPixelId('');
      setAccessToken('');
      setNotes('');
      setSelectedEvents(['PageView']);
      setEnabled(false);
      onCreated?.(data.pixel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="admin-pixels-add-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Radar className="h-4 w-4 text-secondary-700" /> Add tracking pixel
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-secondary-700">Provider</label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger className="mt-1" data-testid="admin-pixels-provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p} value={p}>
                    <span className="flex items-center gap-2">
                      {PROVIDER_LABELS[p] || p}
                      {!PROVIDER_LIVE.has(p) && (
                        <span className="text-[10px] uppercase text-neutral-400 tracking-wider">
                          schema
                        </span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-secondary-700">Pixel ID</label>
            <Input
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              placeholder={provider === 'meta' ? '1234567890 (Meta pixel ID)'
                : provider === 'x' ? 'o1a2b (X measurement id)'
                : provider === 'google' ? 'AW-XXXXXXXXX'
                : 'C1AAABBB'}
              className="mt-1"
              data-testid="admin-pixels-pixel-id-input"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-secondary-700 flex items-center gap-1">
            <KeyRound className="h-3 w-3" /> Access token (for server-side test event, optional)
          </label>
          <Input
            type="password"
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            placeholder={PROVIDER_LIVE.has(provider) ? 'Required to fire live test events' : 'Not used for this provider'}
            className="mt-1"
            data-testid="admin-pixels-access-token-input"
          />
          <div className="text-[10px] text-neutral-400 mt-1">
            Stored server-side. Never returned via GET. Required for the Test button to actually fire.
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-secondary-700">Events to track</label>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {(standardEvents || []).map((ev) => {
              const on = selectedEvents.includes(ev);
              return (
                <button
                  type="button"
                  key={ev}
                  onClick={() => toggleEvent(ev)}
                  className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${on
                    ? 'bg-secondary-500 text-white border-secondary-500'
                    : 'bg-white text-neutral-600 border-neutral-200 hover:border-secondary-300 hover:text-secondary-800'}`}
                  data-testid={`admin-pixels-event-toggle-${ev}`}
                >
                  {ev}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-secondary-700">Notes (optional)</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Campaign context, expected traffic, or reminders for future admins."
            className="mt-1"
            data-testid="admin-pixels-notes-input"
          />
        </div>
        <div className="flex items-center justify-between gap-3 pt-1">
          <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="admin-pixels-enabled-switch" />
            Enabled (inject into public site)
          </label>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-secondary-700 hover:bg-secondary-800 text-white"
            data-testid="admin-pixels-submit-btn"
          >
            {busy ? 'Creating...' : 'Create pixel'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Test log panel
// ---------------------------------------------------------------------------
const PixelTestLogPanel = ({ pixelId, trace, onClose }) => {
  const [expanded, setExpanded] = useState(true);
  if (!trace) return null;
  const ok = trace.fired === true;

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(trace, null, 2)],
      { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pixel-test-${trace.provider}-${pixelId}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className={`mt-3 rounded-lg border p-3 ${ok
        ? 'bg-emerald-50 border-emerald-200'
        : 'bg-amber-50 border-amber-200'}`}
      data-testid={`admin-pixels-test-log-${pixelId}`}
    >
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between gap-2 text-xs text-neutral-700"
      >
        <span className="flex items-center gap-2 font-semibold">
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {ok
            ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
            : <CircleAlert className="h-3.5 w-3.5 text-amber-700" />}
          Test result: {ok ? 'fired' : (trace.reason || 'not fired')}
        </span>
        <span className="text-neutral-500 font-mono">
          {trace.timestamp?.slice(0, 19)?.replace('T', ' ')}
        </span>
      </button>
      {expanded && (
        <div className="mt-3 space-y-2 text-xs">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="bg-white rounded border border-neutral-200 p-2">
              <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Request</div>
              {trace.request ? (
                <pre className="whitespace-pre-wrap break-all text-neutral-700 max-h-56 overflow-auto">
{JSON.stringify(trace.request, null, 2)}
                </pre>
              ) : (
                <div className="text-neutral-400 italic">Not applicable (schema-only provider).</div>
              )}
            </div>
            <div className="bg-white rounded border border-neutral-200 p-2">
              <div className="text-[10px] uppercase tracking-widest text-secondary-700 mb-1">Response</div>
              {trace.response ? (
                <pre className="whitespace-pre-wrap break-all text-neutral-700 max-h-56 overflow-auto">
{JSON.stringify(trace.response, null, 2)}
                </pre>
              ) : (
                <div className="text-neutral-400 italic">No response (see reason above).</div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={downloadJson}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-neutral-200 hover:bg-neutral-50 text-xs text-neutral-700"
              data-testid={`admin-pixels-test-log-download-${pixelId}`}
            >
              <Download className="h-3 w-3" /> Download log (JSON)
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 text-xs"
              data-testid={`admin-pixels-test-log-close-${pixelId}`}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Bundle 49 Task A - Rotate access token dialog
// ---------------------------------------------------------------------------
const _daysBetween = (isoA, isoB = new Date().toISOString()) => {
  try {
    const a = new Date(isoA).getTime();
    const b = new Date(isoB).getTime();
    return Math.max(0, Math.floor((b - a) / (86400 * 1000)));
  } catch (_) { return null; }
};

const TokenRotationDialog = ({ pixel, open, onClose, onRotated }) => {
  const [token, setToken] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      setToken(''); setBusy(false); setResult(null);
    }
  }, [open]);

  const verifyAndSave = async () => {
    if (!token.trim()) {
      toast.error('Paste a token first.');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.patch(
        `/admin/pixels/${pixel.id}/access-token`,
        { new_token: token.trim(), verify: true },
      );
      setResult(data);
      if (data.ok) {
        toast.success('Token rotated. Meta CAPI should now deliver.');
        // Clear the pasted token from memory + UI immediately.
        setToken('');
        if (onRotated) onRotated();
      } else {
        toast.error(
          `Meta rejected this token: ${
            data?.verify?.meta_error_message || 'invalid access token'
          }`,
        );
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Rotate failed';
      toast.error(typeof detail === 'string' ? detail : 'Rotate failed');
    } finally {
      setBusy(false);
    }
  };

  if (!pixel) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="admin-pixels-rotate-dialog">
        <DialogHeader>
          <DialogTitle>Rotate Meta CAPI token</DialogTitle>
          <DialogDescription>
            Meta pixel {pixel.pixel_id}. We verify the new token with Meta
            before saving, so a typo can never overwrite the good one on file.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-700 leading-relaxed">
            <div className="font-semibold text-primary-900 mb-1">
              Where to get a fresh token
            </div>
            Business Manager &rarr; Events Manager &rarr; select this pixel
            &rarr; Settings &rarr; Generate access token. Paste the full
            string below.
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
              New access token
            </label>
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="EAA..."
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              data-testid="admin-pixels-rotate-input"
            />
          </div>
          {result && result.ok && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 p-3 text-xs flex items-start gap-2"
              data-testid="admin-pixels-rotate-success"
            >
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">
                  Token valid{result.verify?.name ? `, connected to ${result.verify.name}` : ''}.
                </div>
                <div className="text-emerald-800 mt-0.5">
                  Saved. New fingerprint: EAA...{result.new_token_last4}.
                </div>
              </div>
            </div>
          )}
          {result && !result.ok && (
            <div
              className="rounded-md border border-red-200 bg-red-50 text-red-900 p-3 text-xs flex items-start gap-2"
              data-testid="admin-pixels-rotate-failed"
            >
              <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Meta rejected this token.</div>
                <div className="text-red-800 mt-0.5">
                  {result.verify?.meta_error_message || result.reason || 'unknown error'}
                </div>
                <div className="text-red-800 mt-1">
                  The old token on file was not overwritten.
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid="admin-pixels-rotate-cancel"
          >
            Close
          </Button>
          <Button
            type="button"
            onClick={verifyAndSave}
            disabled={busy || !token.trim()}
            data-testid="admin-pixels-rotate-submit"
          >
            {busy ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Verifying...</>
            ) : (
              <><ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verify & Save</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Table row
// ---------------------------------------------------------------------------
const PixelRow = ({ pixel, onToggleEnabled, onEdit, onDelete, onTest, onRotate, testTrace, onCloseTrace, busyKey }) => {
  const live = PROVIDER_LIVE.has(pixel.provider);
  const isMeta = pixel.provider === 'meta';
  const rotatedAt = pixel.access_token_rotated_at;
  const daysSince = rotatedAt ? _daysBetween(rotatedAt) : null;
  const rotatedBadge = daysSince == null
    ? null
    : daysSince < 30
      ? 'text-emerald-700'
      : daysSince < 60
        ? 'text-amber-700'
        : 'text-rose-700';
  return (
    <div
      className="border border-neutral-200 rounded-lg p-3 bg-white"
      data-testid={`admin-pixels-row-${pixel.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Badge
            variant="outline"
            className={`${PROVIDER_BADGE[pixel.provider] || 'bg-neutral-50 text-neutral-700'} shrink-0`}
          >
            {PROVIDER_LABELS[pixel.provider] || pixel.provider}
          </Badge>
          <div className="min-w-0">
            <div className="font-mono text-sm font-semibold text-primary-900 truncate">
              {pixel.pixel_id}
            </div>
            <div className="text-xs text-neutral-500 flex items-center gap-2 flex-wrap">
              {(pixel.events || []).length ? (
                pixel.events.slice(0, 5).map((e) => (
                  <span key={e} className="text-neutral-600">{e}</span>
                )).reduce((acc, node, i) => acc.length ? [...acc, <span key={`s${i}`} className="text-neutral-300">·</span>, node] : [node], [])
              ) : (
                <span className="italic text-neutral-400">no events selected</span>
              )}
              {pixel.access_token_configured ? (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <ShieldCheck className="h-3 w-3" /> token on file
                  {pixel.access_token_last4 ? (
                    <span className="text-neutral-500 font-mono">
                      ...{pixel.access_token_last4}
                    </span>
                  ) : null}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <CircleAlert className="h-3 w-3" /> no token
                </span>
              )}
              {rotatedAt && (
                <span
                  className={`inline-flex items-center gap-1 ${rotatedBadge}`}
                  data-testid={`admin-pixels-row-rotated-${pixel.id}`}
                >
                  <KeyRound className="h-3 w-3" />
                  {daysSince === 0 ? 'Rotated today' : `Rotated ${daysSince}d ago`}
                </span>
              )}
              {!live && (
                <span className="uppercase text-[10px] tracking-wider text-neutral-400">
                  schema
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="flex items-center gap-2 text-xs text-neutral-600 cursor-pointer">
            <Switch
              checked={!!pixel.enabled}
              onCheckedChange={() => onToggleEnabled(pixel)}
              data-testid={`admin-pixels-row-enabled-${pixel.id}`}
            />
            {pixel.enabled ? 'Enabled' : 'Disabled'}
          </label>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onTest(pixel)}
            disabled={busyKey === `test:${pixel.id}`}
            className="h-8 text-secondary-800 border-secondary-300 hover:bg-secondary-50"
            data-testid={`admin-pixels-row-test-${pixel.id}`}
          >
            <PlayCircle className="h-3.5 w-3.5 mr-1" />
            {busyKey === `test:${pixel.id}` ? 'Testing...' : 'Test & Verify'}
          </Button>
          {isMeta && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRotate(pixel)}
              className="h-8 text-primary-800 border-primary-300 hover:bg-primary-50"
              data-testid={`admin-pixels-row-rotate-${pixel.id}`}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Rotate token
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(pixel)}
            className="h-8"
            data-testid={`admin-pixels-row-edit-${pixel.id}`}
          >
            <PenLine className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(pixel)}
            className="h-8 text-accent-700 hover:bg-accent-50"
            data-testid={`admin-pixels-row-delete-${pixel.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {testTrace && (
        <PixelTestLogPanel
          pixelId={pixel.id}
          trace={testTrace}
          onClose={() => onCloseTrace(pixel.id)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Edit drawer
// ---------------------------------------------------------------------------
const PixelEditDrawer = ({ pixel, standardEvents, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rotateToken, setRotateToken] = useState(false);

  useEffect(() => {
    if (pixel) {
      setForm({
        pixel_id: pixel.pixel_id || '',
        access_token: '',
        enabled: !!pixel.enabled,
        events: [...(pixel.events || [])],
        notes: pixel.notes || '',
      });
      setRotateToken(false);
    }
  }, [pixel]);

  if (!pixel || !form) return null;

  const toggleEvent = (ev) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev)
        ? f.events.filter((x) => x !== ev)
        : [...f.events, ev],
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        pixel_id: form.pixel_id.trim(),
        enabled: form.enabled,
        events: form.events,
        notes: form.notes.trim(),
      };
      if (rotateToken) {
        payload.access_token = form.access_token.trim();
      }
      const { data } = await api.patch(`/admin/pixels/${pixel.id}`, payload);
      toast.success('Pixel updated');
      onSaved?.(data.pixel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!pixel} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="admin-pixels-edit-drawer">
        <SheetHeader>
          <SheetTitle>Edit pixel</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-4 text-sm">
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <Badge variant="outline" className={PROVIDER_BADGE[pixel.provider]}>
              {PROVIDER_LABELS[pixel.provider] || pixel.provider}
            </Badge>
            <span className="font-mono">id: {pixel.id.slice(0, 10)}...</span>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-secondary-700">Pixel ID</label>
            <Input
              value={form.pixel_id}
              onChange={(e) => setForm({ ...form, pixel_id: e.target.value })}
              className="mt-1"
              data-testid="admin-pixels-edit-pixel-id"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-secondary-700 flex items-center justify-between">
              <span>Access token</span>
              {pixel.access_token_configured && !rotateToken && (
                <button
                  type="button"
                  onClick={() => setRotateToken(true)}
                  className="text-secondary-700 hover:text-secondary-900 normal-case text-[11px]"
                  data-testid="admin-pixels-edit-rotate-token"
                >
                  Rotate token
                </button>
              )}
              {rotateToken && (
                <button
                  type="button"
                  onClick={() => { setRotateToken(false); setForm({ ...form, access_token: '' }); }}
                  className="text-neutral-500 hover:text-neutral-700 normal-case text-[11px]"
                >
                  Cancel rotate
                </button>
              )}
            </label>
            {rotateToken || !pixel.access_token_configured ? (
              <Input
                type="password"
                value={form.access_token}
                onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                placeholder="Paste new access token"
                className="mt-1"
                data-testid="admin-pixels-edit-access-token"
              />
            ) : (
              <div className="mt-1 text-xs text-neutral-500 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3 text-emerald-700" /> token configured (rotate to replace)
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-secondary-700">Events</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(standardEvents || []).map((ev) => {
                const on = form.events.includes(ev);
                return (
                  <button
                    type="button"
                    key={ev}
                    onClick={() => toggleEvent(ev)}
                    className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${on
                      ? 'bg-secondary-500 text-white border-secondary-500'
                      : 'bg-white text-neutral-600 border-neutral-200 hover:border-secondary-300'}`}
                    data-testid={`admin-pixels-edit-event-${ev}`}
                  >
                    {ev}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-secondary-700">Notes</label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="mt-1"
              data-testid="admin-pixels-edit-notes"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
              data-testid="admin-pixels-edit-enabled-switch"
            />
            Enabled (inject into public site)
          </label>
          <div className="flex items-center gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={busy} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={busy}
              className="flex-1 bg-secondary-700 hover:bg-secondary-800 text-white"
              data-testid="admin-pixels-edit-save-btn"
            >
              {busy ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
const AdminPixelsPage = () => {
  const [pixels, setPixels] = useState([]);
  const [providersLive, setProvidersLive] = useState([]);
  const [providersInert, setProvidersInert] = useState([]);
  const [standardEvents, setStandardEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [traces, setTraces] = useState({}); // pixel_id -> trace
  const [busyKey, setBusyKey] = useState('');
  const [rotating, setRotating] = useState(null); // pixel obj being rotated

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/pixels');
      setPixels(data.pixels || []);
      setProvidersLive(data.providers_live || []);
      setProvidersInert(data.providers_inert || []);
      setStandardEvents(data.standard_events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Load pixels failed');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onCreated = () => { load(); };
  const onSaved = () => { setEditing(null); load(); };

  const toggleEnabled = async (pixel) => {
    try {
      await api.patch(`/admin/pixels/${pixel.id}`, { enabled: !pixel.enabled });
      toast.success(!pixel.enabled ? 'Pixel enabled' : 'Pixel disabled');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    }
  };

  const del = async (pixel) => {
    if (!window.confirm(`Delete ${PROVIDER_LABELS[pixel.provider]} pixel ${pixel.pixel_id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/pixels/${pixel.id}`);
      toast.success('Pixel deleted');
      setTraces((cur) => { const c = { ...cur }; delete c[pixel.id]; return c; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const runTest = async (pixel) => {
    setBusyKey(`test:${pixel.id}`);
    try {
      const { data } = await api.post(`/admin/pixels/${pixel.id}/test`);
      setTraces((cur) => ({ ...cur, [pixel.id]: data.trace }));
      if (data.trace?.fired) {
        toast.success('Test event fired successfully');
      } else {
        toast.message(`Test result: ${data.trace?.reason || 'not fired'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally { setBusyKey(''); }
  };

  const closeTrace = (pid) => {
    setTraces((cur) => { const c = { ...cur }; delete c[pid]; return c; });
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="admin-pixels-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">Tracking pixels</h1>
          <p className="text-neutral-600 text-sm mt-1">
            Meta and X pixels fire real conversion events for ad attribution.
            Google and TikTok are stored as schema only for now.
          </p>
        </div>
        <Link
          to="/admin/analytics?tab=pixel"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-800 text-white text-sm font-semibold hover:bg-primary-900"
          data-testid="admin-pixels-analytics-link"
        >
          Open analytics dashboard
        </Link>
      </div>

      <AddPixelCard
        providersLive={providersLive}
        providersInert={providersInert}
        standardEvents={standardEvents}
        onCreated={onCreated}
      />

      <Card className="mt-6" data-testid="admin-pixels-list-card">
        <CardHeader>
          <CardTitle className="text-base">Configured pixels</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="text-sm text-neutral-500 italic">Loading pixels...</div>
          ) : pixels.length === 0 ? (
            <div className="text-sm text-neutral-500 italic" data-testid="admin-pixels-empty-state">
              No pixels configured yet. Add one above to start collecting attribution.
            </div>
          ) : (
            pixels.map((p) => (
              <PixelRow
                key={p.id}
                pixel={p}
                onToggleEnabled={toggleEnabled}
                onEdit={setEditing}
                onDelete={del}
                onTest={runTest}
                onRotate={setRotating}
                testTrace={traces[p.id]}
                onCloseTrace={closeTrace}
                busyKey={busyKey}
              />
            ))
          )}
        </CardContent>
      </Card>

      <PixelEditDrawer
        pixel={editing}
        standardEvents={standardEvents}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />

      <TokenRotationDialog
        pixel={rotating}
        open={!!rotating}
        onClose={() => setRotating(null)}
        onRotated={() => { load(); }}
      />

      {/* Bundle 36 Task C - Live Test Events */}
      <LiveTestEventsCard />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Bundle 36 Task C - Live Test Events card                           */
/* Set a Meta test_event_code and fire synthetic CAPI events.         */
/* ------------------------------------------------------------------ */
const LiveTestEventsCard = () => {
  const [code, setCode] = React.useState('');
  const [current, setCurrent] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [firing, setFiring] = React.useState('');
  const [dispatches, setDispatches] = React.useState([]);
  const load = React.useCallback(async () => {
    try {
      const [cur, hist] = await Promise.all([
        api.get('/admin/pixel/test-event/current'),
        api.get('/admin/pixel/test-event/recent-dispatches?limit=10'),
      ]);
      setCurrent(cur.data);
      setDispatches(hist.data?.dispatches || []);
    } catch (_) {
      // silent
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);
  const setForThirty = async () => {
    if (!code.trim()) { toast.error('Enter a test_event_code first.'); return; }
    setSaving(true);
    try {
      await api.post('/admin/pixel/test-event/set-code',
                      { code: code.trim(), ttl_minutes: 30 });
      toast.success('Test event code active for 30 minutes.');
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to set code.';
      toast.error(typeof msg === 'string' ? msg : 'Failed to set code.');
    } finally { setSaving(false); }
  };
  const dispatch = async (eventName) => {
    if (!current?.code) {
      toast.error('Set an active test_event_code first.');
      return;
    }
    setFiring(eventName);
    try {
      const { data } = await api.post('/admin/pixel/test-event/dispatch', {
        test_event_code: current.code, event_name: eventName,
      });
      if (data.ok) toast.success(`Dispatched ${eventName}. status=${data.status}`);
      else toast.error(`Dispatch returned status=${data.status}`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Dispatch failed.';
      toast.error(typeof msg === 'string' ? msg : 'Dispatch failed.');
    } finally { setFiring(''); }
  };
  const relTime = (iso) => {
    if (!iso) return '';
    try {
      const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
      if (s < 60) return `${s}s`;
      const m = Math.round(s / 60);
      return m < 60 ? `${m}m` : `${Math.round(m / 60)}h`;
    } catch (_) { return ''; }
  };
  return (
    <Card className="border-2 border-secondary-200"
           data-testid="admin-pixels-live-test-events">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span>Live Test Events</span>
          <a
            href="https://business.facebook.com/events_manager2/list/test_events"
            target="_blank" rel="noopener noreferrer"
            className="text-[11px] text-secondary-700 hover:underline"
            data-testid="admin-pixels-live-test-events-meta-link"
          >
            Open Meta Test Events UI
          </a>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">
              test_event_code (from Meta Events Manager)
            </label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="TEST12345"
              data-testid="admin-pixels-test-event-code-input"
            />
          </div>
          <Button
            type="button" onClick={setForThirty} disabled={saving}
            className="bg-primary-800 hover:bg-primary-900 text-white"
            data-testid="admin-pixels-test-event-set-btn"
          >
            {saving ? 'Setting...' : 'Set for 30 min'}
          </Button>
        </div>
        <div className="text-xs">
          {current?.active ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5"
              data-testid="admin-pixels-test-event-current-active"
            >
              Active code: <span className="font-mono font-semibold">{current.code}</span>
              until {new Date(current.expires_at).toLocaleTimeString()}
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-600 px-2 py-0.5"
              data-testid="admin-pixels-test-event-current-inactive"
            >
              No active test_event_code
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {['ViewContent', 'Lead', 'Purchase'].map((n) => (
            <Button
              key={n} type="button" variant="outline" size="sm"
              onClick={() => dispatch(n)} disabled={firing === n}
              data-testid={`admin-pixels-test-event-dispatch-${n}`}
            >
              {firing === n ? 'Firing...' : `Dispatch synthetic ${n}`}
            </Button>
          ))}
        </div>
        {dispatches.length > 0 && (
          <div className="rounded-md border border-neutral-200 overflow-hidden text-xs mt-2"
                data-testid="admin-pixels-test-event-log">
            <table className="w-full">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest text-neutral-600">
                <tr>
                  <th className="text-left px-2 py-1.5">Time</th>
                  <th className="text-left px-2 py-1.5">Event</th>
                  <th className="text-left px-2 py-1.5">Code</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-left px-2 py-1.5">events_received</th>
                </tr>
              </thead>
              <tbody>
                {dispatches.map((d, i) => (
                  <tr key={i} className="border-t border-neutral-100">
                    <td className="px-2 py-1.5 text-neutral-500">{relTime(d.created_at)}</td>
                    <td className="px-2 py-1.5">{d.event_name}</td>
                    <td className="px-2 py-1.5 font-mono">{d.test_event_code}</td>
                    <td className={`px-2 py-1.5 font-mono ${d.status && d.status < 400 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {d.status ?? '-'}
                    </td>
                    <td className="px-2 py-1.5 font-mono">
                      {d.response?.events_received ?? '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPixelsPage;
